import { Sep } from 'components/common/styled/common-styled';
import OverflowOptionButton from 'components/molecules/OverflowButtonGroup/OverflowOptionButton';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import { formatTimeShort } from 'utils/dateTime';
import { formatDuration } from 'features/ScheduleView/scheduleViewUtils';
import { MINUTES_PER_DAY } from 'utils/date/minutesPerDay';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

/** Noon, as minutes into the day: a start before it is a morning one. */
const NOON_MINUTES = MINUTES_PER_DAY / 2;

interface SpecificDurationOptionsProps {
  /** The lengths offered from the chosen start, in minutes. */
  durationOptions: readonly number[];
  onDurationSelect: (duration: number) => void;
  onStartSelect: (time: string) => void;
  selectedDuration?: number;
  selectedTime?: string;
  /** The starts offered on this date, "HH:mm:ss". */
  startOptions: readonly string[];
}

const toMinutesOfDay = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * A resource sold in set lengths, picked as every start on the date — under Morning and Afternoon —
 * and then one of the lengths, each a button, with nothing folded away.
 */
const SpecificDurationOptions = ({
  durationOptions,
  onDurationSelect,
  onStartSelect,
  selectedDuration,
  selectedTime,
  startOptions,
}: SpecificDurationOptionsProps) => {
  const { t: translate } = useTranslation();
  const { locale } = useGlobalDateLocale();
  // The unit words come from the translations, so a length reads in the guest's language.
  const durationLabels = {
    hour: translate('durationHour', { defaultValue: 'hour' }),
    hours: translate('durationHours', { defaultValue: 'hours' }),
    minutes: translate('durationMinutes', { defaultValue: 'min' }),
  };

  const handleDurationSelect = (value: string) =>
    onDurationSelect(Number(value));

  const toStartButton = (time: string) => (
    <OverflowOptionButton
      isActive={selectedTime === time}
      isTimeChip
      key={time}
      label={formatTimeShort(time, locale)}
      onSelect={onStartSelect}
      testId={`start-${time}`}
      value={time}
    />
  );

  const morning = startOptions.filter(
    (time) => toMinutesOfDay(time) < NOON_MINUTES,
  );
  const afternoon = startOptions.filter(
    (time) => toMinutesOfDay(time) >= NOON_MINUTES,
  );

  return (
    <Section data-testid="specific-duration-options">
      {morning.length > 0 && (
        <Group data-testid="start-time-morning">
          <Label>{translate('morning')}</Label>
          <TimeGrid role="group" aria-label={translate('morning')}>
            {morning.map(toStartButton)}
          </TimeGrid>
        </Group>
      )}
      {afternoon.length > 0 && (
        <Group data-testid="start-time-afternoon">
          <Label>{translate('afternoon')}</Label>
          <TimeGrid role="group" aria-label={translate('afternoon')}>
            {afternoon.map(toStartButton)}
          </TimeGrid>
        </Group>
      )}
      {durationOptions.length > 0 && (
        <>
          <Sep />
          <Group data-testid="duration-options">
            <Label data-testid="duration-label">{translate('duration')}</Label>
            <Grid role="group" aria-label={translate('duration')}>
              {durationOptions.map((duration) => (
                <OverflowOptionButton
                  isActive={selectedDuration === duration}
                  key={duration}
                  label={formatDuration(duration, durationLabels)}
                  onSelect={handleDurationSelect}
                  testId={`duration-${duration}`}
                  value={String(duration)}
                />
              ))}
            </Grid>
          </Group>
        </>
      )}
    </Section>
  );
};

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const Label = styled.span`
  font-size: 14px;
  line-height: 16px;
  color: var(--way-colors-contrastColorShades-70);
`;

/** One cell to a time chip, which carries its own size; as many to a row as the width holds. */
const TimeGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(auto-fill, 80px);
  width: 100%;
`;

/** Equal cells, as many to a row as the width holds: eight across a wide column, five in a narrow one. */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(92px, 1fr));
  gap: 8px;
  width: 100%;

  & > button {
    width: 100%;
    min-width: 0;
    padding-left: 8px;
    padding-right: 8px;
    justify-content: center;
    white-space: nowrap;
  }
`;

export default SpecificDurationOptions;
